import { ProjectStatus } from '../types';
import type { UserRole, ServiceFrequency } from '../types';
import type { Clave } from './textos';
import type { Traducir } from './idioma';

// ============================================================
//  Los valores que viven en la base de datos, para leerlos.
//
//  `Draft`, `In Progress`, `Foreman`, `Bi-Weekly`, `material`: son el DATO
//  —lo que se guarda, lo que se compara, lo que filtra— y se estaban
//  pintando tal cual en pantalla. Están en inglés porque el esquema está en
//  inglés, así que la app en español enseñaba «In Progress» y «Foreman».
//
//  Se traduce la ETIQUETA, nunca el dato: lo que se escribe en Supabase y lo
//  que comparan los filtros sigue siendo el valor del enum. Traducir el dato
//  rompería las consultas y lo ya guardado.
//
//  Cada mapa es un `Record<...>` COMPLETO, no `Partial`: añadir un estado o
//  un rol sin nombre deja de compilar. Es la misma red que `titulosDeVista`
//  y `estadosDeEstimado`, y por el mismo motivo — lo que se olvida no es
//  traducir el valor nuevo, es acordarse de que existía una lista aparte.
// ============================================================

export const NOMBRE_DE_ESTADO_PROYECTO: Record<ProjectStatus, Clave> = {
    [ProjectStatus.ACCEPTED]: 'dato.proyecto.aceptado',
    [ProjectStatus.SCHEDULED]: 'dato.proyecto.agendado',
    [ProjectStatus.IN_PROGRESS]: 'dato.proyecto.encurso',
    [ProjectStatus.COMPLETED]: 'dato.proyecto.terminado',
    [ProjectStatus.CANCELLED]: 'dato.proyecto.cancelado',
};

export const NOMBRE_DE_ROL: Record<UserRole, Clave> = {
    Admin: 'dato.rol.admin',
    Office: 'dato.rol.oficina',
    Foreman: 'dato.rol.capataz',
    Laborer: 'dato.rol.trabajador',
    Maintenance: 'dato.rol.servicio',
};

export const NOMBRE_DE_FRECUENCIA: Record<ServiceFrequency, Clave> = {
    Weekly: 'frecuencia.weekly',
    'Bi-Weekly': 'frecuencia.biweekly',
    Monthly: 'frecuencia.monthly',
};

/**
 * Estados de contratos y visitas.
 *
 * Va suelto y no como `Record` de un enum porque no hay enum: son cadenas
 * que llegan de la base y de `WORK_STATUS_TONE`, y conviven con los estados
 * de proyecto en el mismo componente (`WorkStatus` recibe un `string` a
 * secas). Por eso este mapa incluye también los de proyecto: quien pregunta
 * no sabe de cuál de los dos mundos viene el valor que le tocó.
 */
const NOMBRE_DE_ESTADO_TRABAJO: Record<string, Clave> = {
    Active: 'dato.trabajo.activo',
    Paused: 'dato.trabajo.pausado',
    'En Route': 'dato.trabajo.encamino',
    'On Site': 'dato.trabajo.ensitio',
    Skipped: 'dato.trabajo.saltado',
    Accepted: 'dato.proyecto.aceptado',
    Scheduled: 'dato.proyecto.agendado',
    'In Progress': 'dato.proyecto.encurso',
    Completed: 'dato.proyecto.terminado',
    Cancelled: 'dato.proyecto.cancelado',
};

/** Tipos de partida. Llegan en minúscula y a veces con guion bajo. */
const NOMBRE_DE_TIPO_PARTIDA: Record<string, Clave> = {
    material: 'dato.partida.material',
    labor: 'dato.partida.mano',
    equipment: 'dato.partida.equipo',
    equipment_rental: 'dato.partida.equiporenta',
    equipment_owned: 'dato.partida.equipopropio',
    other: 'dato.partida.otro',
};

/* Todos los que se buscan por texto, en un solo sitio. El orden importa poco
   porque las llaves no chocan entre sí. */
const TODOS: Record<string, Clave> = {
    ...NOMBRE_DE_ESTADO_TRABAJO,
    ...NOMBRE_DE_TIPO_PARTIDA,
    ...NOMBRE_DE_FRECUENCIA,
};

/**
 * El nombre legible de un valor de datos, o el valor crudo si no se conoce.
 *
 * Devolver el crudo y no una cadena vacía es deliberado: una fila vieja con
 * un estado que ya no existe se sigue entendiendo en inglés; una celda en
 * blanco no dice nada y encima parece que la app perdió el dato.
 */
export function nombreDeDato(valor: string | null | undefined, t: Traducir): string {
    if (!valor) return '';
    const clave = TODOS[valor] ?? TODOS[valor.toLowerCase()];
    return clave ? t(clave) : valor;
}

/** El rol, por su nombre de oficio. */
export function nombreDeRol(rol: string | null | undefined, t: Traducir): string {
    if (!rol) return '';
    const clave = NOMBRE_DE_ROL[rol as UserRole];
    return clave ? t(clave) : rol;
}
